
import React, { useRef, useState, useEffect } from 'react';
import './App.css';
import Header from './Components/Header';
import StudySection from './Components/StudySection';
import DownloadSection from './Components/DownloadSection';
import labLogo from './Images/LabLogo.png';
import study0 from './Images/study_0.jpeg';
import study1 from './Images/study_1.png';
import study2 from './Images/study_2.png';
import study3 from './Images/study_3.png';

function App() {
  const study0Ref = useRef(null);
  const study1Ref = useRef(null);
  const study2Ref = useRef(null);
  const study3Ref = useRef(null);
  const downloadRef = useRef(null);

  const [studies, setStudies] = useState([]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [allFiles, setAllFiles] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [showAllFiles, setShowAllFiles] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fetch the study list from the Flask backend
    const fetchStudies = async () => {
      try {
        const response = await fetch('/api/studies');
        const data = await response.json();
        if (data.studies) {
          setStudies(data.studies);
        } else {
          setStudies([]);
        }
      } catch (error) {
        console.error("Error fetching studies:", error);
        setStudies([]);
      }
    };

    fetchStudies();
  }, []);

  const handleStudyChange = async (event) => {
    const study = event.target.value;
    setSelectedStudy(study);
    setSelectedFolder('');  // Reset folder selection
    setFiles([]);           // Reset file selection
    setAllFiles([]);        // Reset all files
    setShowAllFiles(false); // Reset show all files

    if (study) {
      try {
        const response = await fetch(`/api/study-folders?study=${study}`);
        const data = await response.json();
        if (data.folders) {
          setFolders(data.folders);
        } else {
          setFolders([]);
        }
      } catch (error) {
        console.error("Error fetching folders:", error);
        setFolders([]);
      }
    }
  };

  const handleFolderChange = async (event) => {
    const folder = event.target.value;
    setSelectedFolder(folder);
    setFiles([]);             // Reset file selection
    setAllFiles([]);          // Reset all files
    setShowAllFiles(false);   // Reset showAll to false initially

    if (selectedStudy && folder) {
      setLoading(true); // Start loading spinner
      try {
        const response = await fetch(`/api/study-files?study=${selectedStudy}&folder=${folder}`);
        const data = await response.json();
        if (data.files) {
          const initialFiles = data.files.slice(0, 2);  // Display the first 2 files by default
          setFiles(initialFiles);
          setAllFiles(data.files);                      // Store all files for "More" functionality
        } else {
          setFiles([]);
          setAllFiles([]);
        }
      } catch (error) {
        console.error("Error fetching files:", error);
        setFiles([]);
        setAllFiles([]);
      } finally {
        setLoading(false); // End loading spinner 
      }     
    }
  };

  const handleDownload = (file) => {
    const fileURL = `https://data.rc.ufl.edu/pub/ramonsun/${selectedStudy}/${selectedFolder}/${file}`;
    const link = document.createElement('a');
    link.href = fileURL;
    link.download = fileURL.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to handle toggling between "More" and "Less"
  const handleShowAllFiles = () => {
    if (showAllFiles) {
      setFiles(allFiles.slice(0, 2)); // Show only the first 2 files
    } else {
      setFiles(allFiles); // Show all files
    }
    setShowAllFiles(!showAllFiles); // Toggle the showAllFiles state
  };

  const scrollToSection = (ref) => {
    ref.current.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="App">
      {/* Header */}
      <Header labLogo={labLogo} />
      {/* Lab Image, Studies and Resources */}
      <main>
        {/* Lab Image */}
        <div className="lab-image" />
        {/* Studies */}
        <StudySection
          refProp={study0Ref}
          title="Vision"
          imageSrc={study0}
          description="Vision — a GUI platform that integrates the analytical pipelines of SAMI and MetaVision3D into a unified, 
            user-friendly desktop application. Vision enables straightforward, linear point-and-click interactions across all 
            analysis steps, including data upload, normalization, multi-omics integration, dimensionality reduction via UMAP, 
            spatial clustering, pathway enrichment, and 3D construction. Vision aims to simplify complex computational processes 
            into intuitive workflows, making spatial metabolomics more accessible to bench scientists who are more familiar with 
            standard desktop applications."
          links={[{ text: "Tutorial Videos", href: "https://www.youtube.com/@UFCASBR" }]}
          onScroll={() => scrollToSection(downloadRef)}
          buttonText="Download Application and Dataset"
        />
        <StudySection
          refProp={study1Ref}
          title="Automated Framework for the Generation of Spatial Metabolome Atlas in 3D"
          imageSrc={study1}
          description="High-resolution spatial imaging is transforming our understanding of foundational biology.
            Spatial metabolomics is an emerging field that enables the dissection of the complex
            metabolic landscape and heterogeneity from a thin tissue section. Currently, spatial metabolism
            highlights the remarkable complexity in two-dimensional space and is poised to be extended
            into the three-dimensional world of biology. Here, we introduce MetaVision3D, a novel pipeline
            driven by computer vision techniques for the transformation of serial 2D MALDI mass spectrometry
            imaging sections into a high-resolution 3D spatial metabolome. Our framework employs
            advanced algorithms for image registration, normalization, and interpolation to enable the
            integration of serial 2D tissue sections, thereby generating a comprehensive 3D model of
            unique diverse metabolites across host tissues at mesoscale. As a proof of principle,
            MetaVision3D was utilized to generate the mouse brain 3D metabolome atlas as an interactive online database and 
            web server to further advance brain metabolism and related research."
          links={[{ text: "Research Paper", href: "https://www.biorxiv.org/content/10.1101/2023.11.27.568931v1" }, 
                  { text: "Spatial Atlas", href: "https://metavision3d.rc.ufl.edu/" }]}
          onScroll={() => scrollToSection(downloadRef)}
        />

        <StudySection
          refProp={study2Ref}
          title="Spatial Metabolome Lipidome and Glycome from a Single Brain Section"
          imageSrc={study2}
          description="Metabolites, lipids, and glycans are fundamental biomolecules involved in complex biological systems.
            They are metabolically channeled through a myriad of pathways and molecular processes that define
            the physiology and pathology of an organism. Here, we present a blueprint for the simultaneous analysis
            of spatial metabolome, lipidome, and glycome from a single tissue section using mass spectrometry
            imaging. Complimenting an original experimental protocol, our workflow includes a computational
            framework called Spatial Augmented Multiomics Interface (Sami) that offers multiomics integration,
            high dimensionality clustering, spatial anatomical mapping with matched multiomics features, and
            metabolic pathway enrichment to providing unprecedented insights into the spatial distribution and
            interaction of these biomolecules in mammalian tissue biology."
          links={[{ text: "Research Paper", href: "https://www.nature.com/articles/s41467-025-59487-7" }]}
          onScroll={() => scrollToSection(downloadRef)}
        />

        <StudySection
          refProp={study3Ref}
          title="Glycogen Metabolism in Non-Small Cell Lung Cancer"
          imageSrc={study3}
          description="This study is a collection of spatial metabolomics analysis of human and mouse non-small cell lung cancer tissues. 
            KrasG12D/p53fl/fl mice were subjected to different diets designed to increase glycogen accumulation in the tissue. 
            Further, groups of KrasG12D/p53fl/fl mice were either bred with an inducible knockout of glycogen synthase or with the glycogen phosphatase, 
            Laforin, knocked out. Finally, the mice lacking glycogen synthase were subjected to a diet designed to induce glycogen accumulation. 
            The data from all of these different groups allowed for examination of the role of glycogen in tumorigenesis. Using MALDI-mass spectrometry imaging, 
            tissues were examined in order to compare glycogen abundance and its role in tumorigenesis in these different tissues."
            links={[{ text: "Research Paper", href: "https://www.nature.com/articles/s42255-025-01243-8" }]}
          onScroll={() => scrollToSection(downloadRef)}
        />

        <DownloadSection
          refProp={downloadRef}
          studies={studies}
          folders={folders}
          files={files}
          allFiles={allFiles}
          selectedStudy={selectedStudy}
          selectedFolder={selectedFolder}
          loading={loading}
          onStudyChange={handleStudyChange}
          onFolderChange={handleFolderChange}
          onFileDownload={handleDownload}
          onToggleFiles={handleShowAllFiles}
        />
      </main>
    </div>
  );
}

export default App;
